// src/components/IndividualNotesDrawer.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../config/supabase';

const { FiX, FiSave, FiLoader, FiUser, FiAlertCircle } = FiIcons;

const DrawerSkeleton = ({ variant = 'overlay', inlineTransition = 'enter' }) => {
  const inlineShell = variant === 'inline';
  const swap = inlineShell && inlineTransition === 'swap';
  return (
    <motion.div
     initial={{ opacity: 0, x: inlineShell && !swap ? 50 : 300 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: inlineShell && !swap ? 50 : 300 }}
      className={`h-full w-full bg-white flex flex-col ${inlineShell ? '' : 'fixed right-0 top-0 w-96 shadow-2xl border-l border-gray-200 z-50'}`}
    >
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="h-5 w-40 bg-gray-200 rounded animate-pulse" />
        <div className="h-8 w-8 bg-gray-200 rounded animate-pulse" />
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="space-y-2">
            <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
            <div className="h-16 w-full bg-gray-200 rounded animate-pulse" />
          </div>
        ))}
      </div>
      <div className="border-t border-gray-200 p-4">
        <div className="h-4 w-48 bg-gray-200 rounded animate-pulse mx-auto" />
      </div>
    </motion.div>
  );
};

/**
 * Props
 * - lessonNoteId (uuid)
 * - lessonId (uuid)
 * - currentUserId (uuid)
 * - onClose()
 * - variant: 'overlay' | 'inline'
 */
export default function IndividualNotesDrawer({
  lessonNoteId,
  lessonId,
  currentUserId,
  onClose,
  variant = 'overlay',
  inlineTransition = 'enter'
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);

  const [schoolId, setSchoolId] = useState(null);
  const [groupId, setGroupId]   = useState(null);

  const [participants, setParticipants] = useState([]); // [{ student_id, student_master_id, student_name }]
  const [contentByMasterId, setContentByMasterId] = useState({}); // { [masterId]: string }

  const disabled = !currentUserId;

  const nonEmptyEntries = useMemo(
    () => Object.entries(contentByMasterId).filter(([, v]) => (v ?? '').trim().length > 0),
    [contentByMasterId]
  );

  useEffect(() => {
    let cancelled = false;
    async function boot() {
      try {
        setLoading(true);
        setError(null);

        // 1) Teacher school
        const { data: me, error: meErr } = await supabase
          .from('users')
          .select('id, school_id')
          .eq('id', currentUserId)
          .single();
        if (meErr) throw meErr;
        if (!cancelled) setSchoolId(me.school_id);

        // 2) Lesson group
        const { data: l, error: lErr } = await supabase
          .from('lessons')
          .select('id, group_id')
          .eq('id', lessonId)
          .single();
        if (lErr) throw lErr;
        if (!cancelled) setGroupId(l?.group_id ?? null);

        // 3) Participants
        const { data: lp, error: lpErr } = await supabase
          .from('lessons_participants')
          .select('student_id, master_student_id')
          .eq('lesson_id', lessonId);
        if (lpErr) throw lpErr;

        let enrolled = [];

        // Canonical identity: master_student_id (works for BOTH solo + group lessons)
        const masterIds = Array.from(
          new Set((lp ?? []).map(r => r.master_student_id).filter(Boolean))
        );

        if (masterIds.length) {
          const { data: masters, error: mastersErr } = await supabase
            .from('students_master_list')
            .select('id, full_name')
            .in('id', masterIds);
          if (mastersErr) throw mastersErr;

          const nameById = Object.fromEntries(
            (masters ?? []).map(m => [
              m.id,
              (m.full_name && String(m.full_name).trim()) || 'Student',
            ])
          );

          // Preserve participant order as returned from lessons_participants
          enrolled = (lp ?? [])
            .filter(r => r.master_student_id)
            .map(r => ({
              student_id: r.student_id ?? null, // may be NULL for group-only participants
              student_master_id: r.master_student_id,
              student_name: nameById[r.master_student_id] || 'Student',
            }));
        }
        if (!cancelled) setParticipants(enrolled);

        // 4) Existing per-student entries
        if (enrolled.length) {
          const masterIds = enrolled.map(p => p.student_master_id).filter(Boolean);
          if (masterIds.length) {
            const { data: existing, error: exErr } = await supabase
              .from('lesson_note_student_notes')
              .select('student_master_id, content')
              .eq('lesson_note_id', lessonNoteId)
              .in('student_master_id', masterIds);
            if (exErr) throw exErr;

            const map = {};
            for (const row of (existing ?? [])) {
              map[row.student_master_id] = row.content ?? '';
            }
            if (!cancelled) setContentByMasterId(prev => ({ ...map, ...prev }));
          }
        }
      } catch (e) {
        console.error('IndividualNotes boot error', e);
        if (!cancelled) setError('Failed to load participants or existing notes.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (lessonNoteId && lessonId && currentUserId) boot();
    return () => { cancelled = true; };
  }, [lessonNoteId, lessonId, currentUserId]);

  const handleChange = (masterId, value) => {
    setContentByMasterId(prev => ({ ...prev, [masterId]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      if (!schoolId) throw new Error('Missing school id for insert.');

      const rows = nonEmptyEntries.map(([masterId, content]) => ({
        school_id: schoolId,
        lesson_note_id: lessonNoteId,
        student_master_id: masterId,
        group_id: groupId ?? null,
        teacher_id: currentUserId,
        content: (content ?? '').trim(),
      }));

      if (!rows.length) {
        onClose?.();
        return;
      }

      const { error: upsertErr } = await supabase
        .from('lesson_note_student_notes')
        .upsert(rows, { onConflict: 'lesson_note_id,student_master_id' })
        .select();
      if (upsertErr) throw upsertErr;

      onClose?.();
    } catch (e) {
      try { console.error('Save individual notes error', JSON.stringify(e, null, 2)); }
      catch { console.error('Save individual notes error', e); }
      setError('Failed to save individual notes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <DrawerSkeleton variant={variant} inlineTransition={inlineTransition} />;

 const inlineShell = variant === 'inline';
  const swap = inlineShell && inlineTransition === 'swap';

  return (
    <motion.div
     initial={{ opacity: 0, x: inlineShell && !swap ? 50 : 300 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: inlineShell && !swap ? 50 : 300 }}
      className={`h-full w-full bg-white flex flex-col ${inlineShell ? '' : 'fixed right-0 top-0 w-96 shadow-2xl border-l border-gray-200 z-50'}`}
    >
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Individual Notes</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={handleSave}
            disabled={disabled || saving}
            className="flex items-center px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
          >
            <SafeIcon icon={saving ? FiLoader : FiSave} className={`mr-1 ${saving ? 'animate-spin' : ''}`} />
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded transition-colors">
            <SafeIcon icon={FiX} className="text-gray-600" />
          </button>
        </div>
      </div>

      {error && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-200 text-red-700 text-sm flex items-center gap-2">
          <SafeIcon icon={FiAlertCircle} />
          <span>{error}</span>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {participants.length === 0 ? (
          <div className="text-sm text-gray-600">No participants found for this lesson.</div>
        ) : (
          participants.map(p => (
            <div key={p.student_master_id} className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <SafeIcon icon={FiUser} className="text-gray-500" />
                <span className="truncate">{p.student_name}</span>
              </label>
              <textarea
                value={contentByMasterId[p.student_master_id] ?? ''}
                onChange={(e) => handleChange(p.student_master_id, e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="Write a private note for this student…"
                disabled={disabled}
              />
            </div>
          ))
        )}
      </div>

      <div className="border-t border-gray-200 p-4 text-center text-xs text-gray-500">
        Only students with content will have a private note created.
      </div>
    </motion.div>
  );
}